import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItemConfirm,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useToast, useToastError } from "@/components/ui/use-toast";
import { useAdminTeamsEmitter } from "@/hooks/useAdminTeamsEvents";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import { useSetupSurveyOptions } from "@/lib/setup-survey";
import { AdminTeamDTO } from "@shaarp/common";
import { ChevronDownIcon } from "lucide-react";
import { useState } from "react";
import { RiSurveyLine } from "react-icons/ri";

export default function DataTableCellAdminsSurvey({
  team,
}: {
  team: AdminTeamDTO;
}) {
  const [open, setOpen] = useState(false);
  const { comeFromOptions, whatWaitingForOptions } = useSetupSurveyOptions();
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();
  const { toast } = useToast();
  const { refreshTeams } = useAdminTeamsEmitter();

  const doUpdateSetupCompleted = async (setupCompleted: boolean) => {
    try {
      await api.post(`/admin/team/${team.id}/setup`, { setupCompleted });
      toast({
        variant: "default",
        title: t("admin.dataTableAdminTeams.toast.setupUpdated"),
        description: setupCompleted
          ? t("admin.dataTableAdminTeams.toast.setupMarkedCompleted")
          : t("admin.dataTableAdminTeams.toast.setupMarkedInProgress"),
      });
      refreshTeams();
    } catch {
      toastError();
    }
  };

  return (
    <>
      <div className="flex flex-row gap-1">
        <Tooltip delayDuration={0}>
          <TooltipTrigger>
            <Badge variant="outline">
              {team.setupType === "AUTO" ? "A" : "M"}
            </Badge>
          </TooltipTrigger>
          <TooltipContent>
            {team.setupType === "AUTO"
              ? t("admin.dataTableAdminTeams.tooltips.autoCreated")
              : t("admin.dataTableAdminTeams.tooltips.manualCreated")}
          </TooltipContent>
        </Tooltip>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Badge
              variant={
                team.status === "enabled"
                  ? team.setupCompleted
                    ? "success"
                    : "warning"
                  : "outline"
              }
              className="cursor-pointer whitespace-nowrap"
            >
              {team.setupCompleted
                ? t("admin.dataTableAdminTeams.badges.setupCompleted")
                : t("admin.dataTableAdminTeams.badges.setupInProgress")}
              <ChevronDownIcon className="ml-2 w-3 h-3" />
            </Badge>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start">
            {team.setupCompleted ? (
              <DropdownMenuItemConfirm
                text={t(
                  "admin.dataTableAdminTeams.setupMenu.confirmForceInProgress",
                )}
                description={t(
                  "admin.dataTableAdminTeams.setupMenu.forceInProgressDescription",
                )}
                btnConfirmText={t(
                  "admin.dataTableAdminTeams.setupMenu.continue",
                )}
                onConfirm={() => doUpdateSetupCompleted(false)}
              >
                {t("admin.dataTableAdminTeams.setupMenu.forceInProgress")}
              </DropdownMenuItemConfirm>
            ) : (
              <DropdownMenuItemConfirm
                text={t(
                  "admin.dataTableAdminTeams.setupMenu.confirmForceCompleted",
                )}
                description={t(
                  "admin.dataTableAdminTeams.setupMenu.forceCompletedDescription",
                )}
                btnConfirmText={t(
                  "admin.dataTableAdminTeams.setupMenu.continue",
                )}
                onConfirm={() => doUpdateSetupCompleted(true)}
              >
                {t("admin.dataTableAdminTeams.setupMenu.forceCompleted")}
              </DropdownMenuItemConfirm>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
        {team.setupSurvey ? (
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <Badge variant="success" onClick={() => setOpen(true)}>
                <RiSurveyLine className="w-4 h-4" />
                <ChevronDownIcon className="ml-2 w-3 h-3" />
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {t("admin.dataTableAdminTeams.tooltips.viewSurvey")}
            </TooltipContent>
          </Tooltip>
        ) : (
          <Tooltip delayDuration={0}>
            <TooltipTrigger>
              <Badge variant="outline">
                <RiSurveyLine className="w-4 h-4" />
              </Badge>
            </TooltipTrigger>
            <TooltipContent>
              {t("admin.dataTableAdminTeams.tooltips.noSurvey")}
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>
              {t("admin.dataTableAdminTeams.survey.title", {
                teamName: team.name,
              })}
            </DialogTitle>
          </DialogHeader>
          <div>
            {!team.setupSurvey ? (
              t("admin.dataTableAdminTeams.survey.notFilled")
            ) : (
              <>
                <ul>
                  <li>
                    <span className="font-semibold">
                      {t("admin.dataTableAdminTeams.survey.fields.origin")}
                    </span>
                    :{" "}
                    {team.setupSurvey.comeFrom
                      .map(
                        (v) =>
                          comeFromOptions.find((f) => f.value === v)?.text || v,
                      )
                      .join(", ")}
                  </li>
                  {team.setupSurvey.comeFromOther && (
                    <li>
                      <span className="font-semibold">
                        {t(
                          "admin.dataTableAdminTeams.survey.fields.otherOrigin",
                        )}
                      </span>
                      : {team.setupSurvey.comeFromOther}
                    </li>
                  )}
                  {team.setupSurvey.companySize && (
                    <li>
                      <span className="font-semibold">
                        {t(
                          "admin.dataTableAdminTeams.survey.fields.companySize",
                        )}
                      </span>
                      : {team.setupSurvey.companySize.toString()}
                    </li>
                  )}
                  {team.setupSurvey.teamSize && (
                    <li>
                      <span className="font-semibold">
                        {t(
                          "admin.dataTableAdminTeams.survey.fields.teamSize",
                        )}{" "}
                      </span>
                      : {team.setupSurvey.teamSize.toString()}
                    </li>
                  )}
                  {team.setupSurvey.potentialUsersCount && (
                    <li>
                      <span className="font-semibold">
                        {t(
                          "admin.dataTableAdminTeams.survey.fields.potentialUsersCount",
                        )}
                      </span>
                      : {team.setupSurvey.potentialUsersCount.toString()}
                    </li>
                  )}
                  <li>
                    <span className="font-semibold">
                      {t(
                        "admin.dataTableAdminTeams.survey.fields.expectations",
                      )}
                    </span>
                    :{" "}
                    {team.setupSurvey.whatWaitingFor
                      .map(
                        (v) =>
                          whatWaitingForOptions.find((f) => f.value === v)
                            ?.text || v,
                      )
                      .join(", ")}
                  </li>
                  {team.setupSurvey.whatWaitingForOther && (
                    <li>
                      <span className="font-semibold">
                        {t(
                          "admin.dataTableAdminTeams.survey.fields.otherExpectations",
                        )}
                      </span>
                      : {team.setupSurvey.whatWaitingForOther}
                    </li>
                  )}
                  {team.setupSurvey.comment && (
                    <li>
                      <span className="font-semibold">
                        {t("admin.dataTableAdminTeams.survey.fields.comment")}
                      </span>
                      : {team.setupSurvey.comment.toString()}
                    </li>
                  )}
                </ul>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
